import { Icons } from '@/components/icons';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { api } from '@/lib/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useInfiniteFilterList } from '../hooks/use-infinite-filter-list';
import type { GlobalFilterItem } from '../types';
import {
  GlobalFilerListItemSkeleton,
  GlobalFilterListItem,
} from './filter-list-item';

export function FilterExternalClients({
  search,
  onToggle,
  filteredItems,
}: {
  search: string;
  onToggle: (id: string, displayString: string) => void;
  filteredItems: GlobalFilterItem[];
}) {
  const {
    items: externalClients,
    isLoading,
    hasNextPage,
    sentryRef,
  } = useInfiniteFilterList(
    useInfiniteQuery(
      api.organization_externalclient.query.infinite.queryOptions({
        params: {
          pageSize: 25,
          search: search || undefined,
        },
      })
    )
  );

  if (isLoading) {
    return Array.from({ length: 10 }).map((_, index) => (
      <GlobalFilerListItemSkeleton key={index} />
    ));
  }

  if (!externalClients.length) {
    return (
      <div className='flex h-full flex-col'>
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <Icons.company />
            </EmptyMedia>
            <EmptyTitle>Inga uppdragsgivare hittades</EmptyTitle>
            <EmptyDescription>
              Vi kunde inte hitta några uppdragsgivare.{' '}
              {search.length > 0 && 'Försök med en annan sökterm.'}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div>
      {externalClients.map((externalClient) => {
        const isSelected = Boolean(
          filteredItems.find(
            (fi) =>
              fi.type === 'filter_external_clients' &&
              fi.id === externalClient.id
          )
        );

        return (
          <GlobalFilterListItem
            key={externalClient.id}
            item={externalClient}
            isSelected={isSelected}
            onToggle={onToggle}
          />
        );
      })}

      {hasNextPage && (
        <div ref={sentryRef}>
          <GlobalFilerListItemSkeleton />
        </div>
      )}
    </div>
  );
}
